// YAML converter utilities
// Tiny YAML parser + serializer (no external deps)

// ─── YAML Parser ─────────────────────────────────────────────────────────────

export function parseYaml(yamlStr) {
  const lines = yamlStr.split('\n');
  return parseBlock(lines, 0).value;
}

function parseBlock(lines, baseIndent) {
  // Collect non-empty, non-comment lines at >= baseIndent
  const meaningful = [];
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const trimmed = raw.trimStart();
    if (!trimmed || trimmed.startsWith('#')) continue;
    const indent = raw.length - raw.trimStart().length;
    meaningful.push({ indent, trimmed, raw, i });
  }
  if (meaningful.length === 0) return { value: null };

  // Detect if it's a list block
  if (meaningful[0].trimmed.startsWith('- ')) {
    const arr = [];
    let currentItem = null;
    let itemIndent = meaningful[0].indent;
    let itemLines = [];

    for (const { indent, trimmed } of meaningful) {
      if (indent === itemIndent && trimmed.startsWith('- ')) {
        if (currentItem !== null || itemLines.length > 0) {
          arr.push(resolveItem(currentItem, itemLines, itemIndent + 2));
        }
        currentItem = trimmed.slice(2).trim();
        itemLines = [];
      } else {
        itemLines.push({ indent, trimmed });
      }
    }
    arr.push(resolveItem(currentItem, itemLines, itemIndent + 2));
    return { value: arr };
  }

  // It's a mapping
  const obj = {};
  const topItems = [];
  const topIndent = meaningful[0].indent;

  let currentKey = null;
  let childLines = [];

  for (const { indent, trimmed } of meaningful) {
    if (indent === topIndent && trimmed.includes(':')) {
      if (currentKey !== null) {
        topItems.push({ key: currentKey, childLines: [...childLines] });
      }
      const colonIdx = trimmed.indexOf(':');
      currentKey = trimmed.slice(0, colonIdx).trim().replace(/^["']|["']$/g, '');
      const rest = trimmed.slice(colonIdx + 1).trim();
      childLines = rest ? [{ indent: topIndent + 2, trimmed: rest }] : [];
    } else if (currentKey !== null && indent > topIndent) {
      childLines.push({ indent, trimmed });
    }
  }
  if (currentKey !== null) topItems.push({ key: currentKey, childLines });

  for (const { key, childLines: cLines } of topItems) {
    if (cLines.length === 0) {
      obj[key] = null;
    } else if (cLines.length === 1 && !cLines[0].trimmed.includes(':') && !cLines[0].trimmed.startsWith('- ')) {
      obj[key] = parseScalar(cLines[0].trimmed);
    } else {
      const subLines = cLines.map(l => ' '.repeat(l.indent) + l.trimmed);
      obj[key] = parseBlock(subLines, cLines[0]?.indent ?? 0).value;
    }
  }
  return { value: obj };
}

function resolveItem(inlineVal, childLines, childIndent) {
  if (childLines.length === 0) return parseScalar(inlineVal ?? '');
  if (inlineVal) {
    // inline map continuation e.g. "- key: val"
    const subLines = [' '.repeat(childIndent - 2) + inlineVal, ...childLines.map(l => ' '.repeat(l.indent) + l.trimmed)];
    return parseBlock(subLines, childIndent - 2).value;
  }
  const subLines = childLines.map(l => ' '.repeat(l.indent) + l.trimmed);
  return parseBlock(subLines, childIndent).value;
}

function parseScalar(str) {
  if (str === 'null' || str === '~') return null;
  if (str === 'true') return true;
  if (str === 'false') return false;
  if ((str.startsWith('"') && str.endsWith('"')) || (str.startsWith("'") && str.endsWith("'"))) {
    return str.slice(1, -1);
  }
  const n = Number(str);
  if (!isNaN(n) && str !== '') return n;
  return str;
}

// ─── YAML Serializer (reuse from jsonConverters) ──────────────────────────────

function serializeYaml(val, indent) {
  const pad = ' '.repeat(indent);
  if (val === null) return 'null';
  if (typeof val === 'boolean' || typeof val === 'number') return String(val);
  if (typeof val === 'string') {
    if (/[\n:{}[\],&*#?|<>=!%@`]/.test(val) || /^\s|\s$/.test(val) || val === '')
      return `"${val.replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
    return val;
  }
  if (Array.isArray(val)) {
    if (val.length === 0) return '[]';
    return val.map(item => {
      const s = serializeYaml(item, indent + 2);
      if (typeof item === 'object' && item !== null && !Array.isArray(item)) {
        const lines = s.split('\n');
        return `${pad}- ${lines[0]}\n${lines.slice(1).join('\n')}`;
      }
      return `${pad}- ${s}`;
    }).join('\n');
  }
  if (typeof val === 'object') {
    const keys = Object.keys(val);
    if (keys.length === 0) return '{}';
    return keys.map(k => {
      const v = val[k];
      const ks = /[:\s#]/.test(k) ? `"${k}"` : k;
      if (typeof v === 'object' && v !== null) {
        const s = serializeYaml(v, indent + 2);
        return `${pad}${ks}:\n${s}`;
      }
      return `${pad}${ks}: ${serializeYaml(v, indent + 2)}`;
    }).join('\n');
  }
  return String(val);
}

// ─── Helpers (duplicated to keep this file self-contained) ───────────────────

function toPascalCase(str) {
  return str.replace(/(^\w|[-_\s]\w)/g, m => m.replace(/[-_\s]/, '').toUpperCase());
}
function toCamelCase(str) {
  const p = toPascalCase(str);
  return p.charAt(0).toLowerCase() + p.slice(1);
}
function toSnakeCase(str) {
  return str.replace(/([A-Z])/g, '_$1').replace(/^_/, '').replace(/[-\s]/g, '_').toLowerCase();
}

// ─── YAML → Pike ─────────────────────────────────────────────────────────────

export function yamlToPike(input) {
  const obj = parseYaml(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `  ${getPikeType(k, v)} ${k};`);
    classes[name] = `class ${name} {\n${fields.join('\n')}\n}`;
  }
  function getPikeType(key, val) {
    if (val === null) return 'mixed';
    if (Array.isArray(val)) return 'array';
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'int';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float';
    return 'string';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── YAML Minifier ────────────────────────────────────────────────────────────

export function yamlMinify(input) {
  const obj = parseYaml(input);
  function inline(val) {
    if (val === null) return 'null';
    if (typeof val === 'boolean' || typeof val === 'number') return String(val);
    if (typeof val === 'string') return val.includes(':') ? `"${val}"` : val;
    if (Array.isArray(val)) return `[${val.map(inline).join(', ')}]`;
    if (typeof val === 'object') return `{${Object.entries(val).map(([k, v]) => `${k}: ${inline(v)}`).join(', ')}}`;
    return String(val);
  }
  return inline(obj);
}

// ─── YAML → TypeScript ────────────────────────────────────────────────────────

export function yamlToTypeScript(input) {
  const obj = parseYaml(input);
  const interfaces = {};
  function buildInterface(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `  ${k}: ${getTsType(k, v)};`);
    interfaces[name] = `interface ${name} {\n${fields.join('\n')}\n}`;
  }
  function getTsType(key, val) {
    if (val === null) return 'null';
    if (Array.isArray(val)) return val.length === 0 ? 'any[]' : `${getTsType(key, val[0])}[]`;
    if (typeof val === 'object') { buildInterface(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'boolean';
    if (typeof val === 'number') return 'number';
    return 'string';
  }
  buildInterface('Root', obj);
  return Object.values(interfaces).reverse().join('\n\n');
}

// ─── YAML → JSON Schema ───────────────────────────────────────────────────────

export function yamlToJsonSchema(input) {
  const obj = parseYaml(input);
  function toSchema(val) {
    if (val === null) return { type: 'null' };
    if (Array.isArray(val)) return { type: 'array', items: val.length > 0 ? toSchema(val[0]) : {} };
    if (typeof val === 'object') {
      const props = {};
      for (const [k, v] of Object.entries(val)) props[k] = toSchema(v);
      return { type: 'object', properties: props, required: Object.keys(val) };
    }
    return { type: typeof val };
  }
  return JSON.stringify({ $schema: 'http://json-schema.org/draft-07/schema#', ...toSchema(obj) }, null, 2);
}

// ─── YAML → PHP ───────────────────────────────────────────────────────────────

export function yamlToPhp(input) {
  const obj = parseYaml(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const props = Object.entries(o).map(([k, v]) => `    public ${getPhpType(k, v)} $${toCamelCase(k)};`);
    classes[name] = `class ${name}\n{\n${props.join('\n')}\n}`;
  }
  function getPhpType(key, val) {
    if (val === null) return '?mixed';
    if (Array.isArray(val)) return 'array';
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float';
    return 'string';
  }
  buildClass('Root', obj);
  return `<?php\n\n` + Object.values(classes).reverse().join('\n\n');
}

// ─── YAML → TOML ─────────────────────────────────────────────────────────────

export function yamlToToml(input) {
  const obj = parseYaml(input);
  function serializeToml(o, prefix) {
    let simple = '';
    let complex = '';
    for (const [k, v] of Object.entries(o)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (v === null || typeof v === 'boolean' || typeof v === 'number') {
        simple += `${k} = ${JSON.stringify(v)}\n`;
      } else if (typeof v === 'string') {
        simple += `${k} = ${JSON.stringify(v)}\n`;
      } else if (Array.isArray(v)) {
        if (v.length === 0 || typeof v[0] !== 'object') {
          simple += `${k} = [${v.map(i => JSON.stringify(i)).join(', ')}]\n`;
        } else {
          // eslint-disable-next-line no-loop-func
          v.forEach((item, index) => { 
            const itemComplex = `\n[[${key}]]\n${serializeToml(item, '')}`;
            complex += itemComplex;
          });
        }
      } else if (typeof v === 'object') {
        complex += `\n[${key}]\n${serializeToml(v, key)}`;
      }
    }
    return simple + complex;
  }
  return serializeToml(obj, '');
}

// ─── YAML → Rust ─────────────────────────────────────────────────────────────

export function yamlToRust(input) {
  const obj = parseYaml(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    pub ${toSnakeCase(k)}: ${getRustType(k, v)},`);
    structs[name] = `#[derive(Debug, Serialize, Deserialize)]\npub struct ${name} {\n${fields.join('\n')}\n}`;
  }
  function getRustType(key, val) {
    if (val === null) return 'Option<serde_json::Value>';
    if (Array.isArray(val)) return val.length === 0 ? 'Vec<serde_json::Value>' : `Vec<${getRustType(key, val[0])}>`;
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'i64' : 'f64';
    return 'String';
  }
  buildStruct('Root', obj);
  return `use serde::{Deserialize, Serialize};\n\n` + Object.values(structs).reverse().join('\n\n');
}

// ─── YAML Merge ───────────────────────────────────────────────────────────────

export function yamlMerge(input1, input2) {
  const a = parseYaml(input1);
  const b = parseYaml(input2);
  function deepMerge(x, y) {
    if (Array.isArray(x) && Array.isArray(y)) return [...x, ...y];
    if (typeof x === 'object' && x && typeof y === 'object' && y) {
      const r = { ...x };
      for (const [k, v] of Object.entries(y)) r[k] = k in r ? deepMerge(r[k], v) : v;
      return r;
    }
    return y;
  }
  return serializeYaml(deepMerge(a, b), 0);
}

// ─── YAML → Ruby ─────────────────────────────────────────────────────────────

export function yamlToRuby(input) {
  const obj = parseYaml(input);
  const modules = {};
  function buildModule(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const attrs = Object.keys(o).map(k => `    attr_accessor :${toSnakeCase(k)}`);
    const init = Object.keys(o).map(k => `      @${toSnakeCase(k)} = data[:${toSnakeCase(k)}]`);
    modules[name] = `  class ${name}\n${attrs.join('\n')}\n\n    def initialize(data = {})\n${init.join('\n')}\n    end\n  end`;
  }
  buildModule('Root', obj);
  return `module GeneratedModels\n${Object.values(modules).reverse().join('\n\n')}\nend`;
}

// ─── Properties → YAML ────────────────────────────────────────────────────────

export function propertiesToYaml(input) {
  const obj = {};
  for (const line of input.split('\n')) {
    const t = line.trim();
    if (!t || t.startsWith('#')) continue;
    const eqIdx = t.indexOf('=');
    if (eqIdx === -1) continue;
    const k = t.slice(0, eqIdx).trim();
    const v = t.slice(eqIdx + 1).trim();
    const parts = k.split('.');
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!(parts[i] in cur)) cur[parts[i]] = {};
      cur = cur[parts[i]];
    }
    cur[parts[parts.length - 1]] = v;
  }
  return serializeYaml(obj, 0);
}

// ─── YAML → CSV ───────────────────────────────────────────────────────────────

export function yamlToCsv(input) {
  const obj = parseYaml(input);
  const arr = Array.isArray(obj) ? obj : [obj];
  if (arr.length === 0) return '';
  const headers = [...new Set(arr.flatMap(r => Object.keys(r)))];
  const escape = v => {
    const s = v == null ? '' : String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = arr.map(r => headers.map(h => escape(r[h])).join(','));
  return [headers.join(','), ...rows].join('\n');
}

// ─── YAML Viewer ─────────────────────────────────────────────────────────────

export function yamlViewer(input) {
  const obj = parseYaml(input);
  return JSON.stringify(obj, null, 2);
}

// ─── YAML → Haskell ───────────────────────────────────────────────────────────

export function yamlToHaskell(input) {
  const obj = parseYaml(input);
  const modules = {};
  function buildData(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `  , ${toCamelCase(k)} :: ${getHsType(k, v)}`);
    const first = fields[0] ? fields[0].replace(/^ {2},/, '  {') : '  {}';
    modules[name] = `data ${name} = ${name}\n${first}\n${fields.slice(1).join('\n')}\n  } deriving (Show, Eq)`;
  }
  function getHsType(key, val) {
    if (val === null) return 'Maybe ()';
    if (Array.isArray(val)) return val.length === 0 ? '[()]' : `[${getHsType(key, val[0])}]`;
    if (typeof val === 'object') { buildData(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'Text';
  }
  buildData('Root', obj);
  return `module Root where\n\nimport Data.Text (Text)\n\n` + Object.values(modules).reverse().join('\n\n');
}

// ─── YAML → XML ───────────────────────────────────────────────────────────────

export function yamlToXml(input) {
  const obj = parseYaml(input);
  function toXml(val, tag) {
    const open = `<${tag}>`;
    const close = `</${tag}>`;
    if (val === null) return `${open}${close}`;
    if (Array.isArray(val)) return val.map(item => toXml(item, tag.replace(/s$/, ''))).join('\n');
    if (typeof val === 'object') {
      const inner = Object.entries(val).map(([k, v]) => toXml(v, k)).join('\n');
      return `${open}\n${inner.split('\n').map(l => '  ' + l).join('\n')}\n${close}`;
    }
    return `${open}${val}${close}`;
  }
  return `<?xml version="1.0" encoding="UTF-8"?>\n${toXml(obj, 'root')}`;
}

// ─── YAML → Properties ────────────────────────────────────────────────────────

export function yamlToProperties(input) {
  const obj = parseYaml(input);
  const lines = [];
  function flatten(o, prefix) {
    for (const [k, v] of Object.entries(o)) {
      const key = prefix ? `${prefix}.${k}` : k;
      if (typeof v === 'object' && v !== null && !Array.isArray(v)) flatten(v, key);
      else lines.push(`${key}=${v == null ? '' : v}`);
    }
  }
  flatten(obj, '');
  return lines.join('\n');
}

// ─── YAML Generator ───────────────────────────────────────────────────────────

export function yamlGenerate() {
  const sample = {
    app: {
      name: '示例应用',
      version: '1.0.0',
      debug: false
    },
    server: {
      host: 'localhost',
      port: 8080
    },
    database: {
      host: 'db.example.com',
      port: 5432,
      name: 'mydb'
    },
    features: ['feature1', 'feature2']
  };
  return serializeYaml(sample, 0);
}

// ─── YAML → Sarcastic ────────────────────────────────────────────────────────

export function yamlToSarcastic(input) {
  const obj = parseYaml(input);
  function sarcastify(val) {
    if (typeof val === 'string') return val.split('').map((c, i) => i % 2 === 0 ? c.toLowerCase() : c.toUpperCase()).join('');
    if (Array.isArray(val)) return val.map(sarcastify);
    if (typeof val === 'object' && val !== null) {
      const r = {};
      for (const [k, v] of Object.entries(val)) r[k] = sarcastify(v);
      return r;
    }
    return val;
  }
  return serializeYaml(sarcastify(obj), 0);
}

// ─── YAML → Elm ───────────────────────────────────────────────────────────────

export function yamlToElm(input) {
  const obj = parseYaml(input);
  const types = {};
  function buildType(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    , ${toCamelCase(k)} : ${getElmType(k, v)}`);
    const first = fields[0] ? `    { ${fields[0].slice(6)}` : '    {}';
    types[name] = `type alias ${name} =\n${first}\n${fields.slice(1).join('\n')}\n    }`;
  }
  function getElmType(key, val) {
    if (val === null) return 'Maybe ()';
    if (Array.isArray(val)) return val.length === 0 ? 'List ()' : `List ${getElmType(key, val[0])}`;
    if (typeof val === 'object') { buildType(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Float';
    return 'String';
  }
  buildType('Root', obj);
  return `module Root exposing (..)\n\n` + Object.values(types).reverse().join('\n\n');
}

// ─── YAML → Java Bean ────────────────────────────────────────────────────────

export function yamlToJava(input) {
  const obj = parseYaml(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    private ${getJavaType(k, v)} ${toCamelCase(k)};`);
    const getters = Object.entries(o).map(([k, v]) => {
      const jt = getJavaType(k, v);
      const cc = toCamelCase(k);
      const pc = toPascalCase(k);
      return `    public ${jt} get${pc}() { return ${cc}; }\n    public void set${pc}(${jt} ${cc}) { this.${cc} = ${cc}; }`;
    });
    classes[name] = `public class ${name} {\n${fields.join('\n')}\n\n${getters.join('\n\n')}\n}`;
  }
  function getJavaType(key, val) {
    if (val === null) return 'Object';
    if (Array.isArray(val)) return val.length === 0 ? 'List<Object>' : `List<${getJavaType(key, val[0])}>`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Boolean';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Integer' : 'Double';
    return 'String';
  }
  buildClass('Root', obj);
  return `import java.util.List;\n\n` + Object.values(classes).reverse().join('\n\n');
}

// ─── YAML → Swift ────────────────────────────────────────────────────────────

export function yamlToSwift(input) {
  const obj = parseYaml(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const props = Object.entries(o).map(([k, v]) => `    let ${toCamelCase(k)}: ${getSwiftType(k, v)}`);
    structs[name] = `struct ${name}: Codable {\n${props.join('\n')}\n}`;
  }
  function getSwiftType(key, val) {
    if (val === null) return 'Any?';
    if (Array.isArray(val)) return val.length === 0 ? '[Any]' : `[${getSwiftType(key, val[0])}]`;
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'String';
  }
  buildStruct('Root', obj);
  return Object.values(structs).reverse().join('\n\n');
}

// ─── YAML → IO-TS ────────────────────────────────────────────────────────────

export function yamlToIoTs(input) {
  const obj = parseYaml(input);
  const codecs = {};
  function buildCodec(name, val) {
    if (typeof val !== 'object' || val === null || Array.isArray(val)) return ioTsType(name, val);
    const fields = Object.entries(val).map(([k, v]) => `  ${k}: ${buildCodec(k, v)},`);
    codecs[name] = `const ${toCamelCase(name)}Codec = t.type({\n${fields.join('\n')}\n});`;
    return `${toCamelCase(name)}Codec`;
  }
  function ioTsType(key, val) {
    if (val === null) return 't.null';
    if (Array.isArray(val)) return val.length === 0 ? 't.array(t.unknown)' : `t.array(${ioTsType(key, val[0])})`;
    if (typeof val === 'object') { buildCodec(toPascalCase(key), val); return `${toCamelCase(key)}Codec`; }
    if (typeof val === 'boolean') return 't.boolean';
    if (typeof val === 'number') return 't.number';
    return 't.string';
  }
  buildCodec('Root', obj);
  return `import * as t from 'io-ts';\n\n` + Object.values(codecs).reverse().join('\n\n') + `\n\nexport type Root = t.TypeOf<typeof rootCodec>;`;
}

// ─── YAML → Python ───────────────────────────────────────────────────────────

export function yamlToPython(input) {
  const obj = parseYaml(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    ${toSnakeCase(k)}: ${getPyType(k, v)}`);
    classes[name] = `@dataclass\nclass ${name}:\n${fields.join('\n')}`;
  }
  function getPyType(key, val) {
    if (val === null) return 'Optional[Any]';
    if (Array.isArray(val)) return val.length === 0 ? 'List[Any]' : `List[${getPyType(key, val[0])}]`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float';
    return 'str';
  }
  buildClass('Root', obj);
  return `from dataclasses import dataclass\nfrom typing import Any, List, Optional\n\n` + Object.values(classes).reverse().join('\n\n');
}

// ─── YAML → Scala ────────────────────────────────────────────────────────────

export function yamlToScala(input) {
  const obj = parseYaml(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const params = Object.entries(o).map(([k, v]) => `  ${toCamelCase(k)}: ${getScalaType(k, v)}`);
    classes[name] = `case class ${name}(\n${params.join(',\n')}\n)`;
  }
  function getScalaType(key, val) {
    if (val === null) return 'Option[Any]';
    if (Array.isArray(val)) return val.length === 0 ? 'List[Any]' : `List[${getScalaType(key, val[0])}]`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Boolean';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'String';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── YAML Diff / Comparator ───────────────────────────────────────────────────

export function yamlCompare(input1, input2) {
  const a = parseYaml(input1);
  const b = parseYaml(input2);
  const diffs = [];
  function compare(x, y, path) {
    if (JSON.stringify(x) === JSON.stringify(y)) return;
    if (typeof x !== typeof y) { diffs.push({ path, left: x, right: y, type: 'type_mismatch' }); return; }
    if (typeof x !== 'object' || x === null) { diffs.push({ path, left: x, right: y, type: 'value_changed' }); return; }
    const keys = new Set([...Object.keys(x), ...Object.keys(y)]);
    for (const k of keys) {
      const p = path ? `${path}.${k}` : k;
      if (!(k in x)) diffs.push({ path: p, left: undefined, right: y[k], type: 'added' });
      else if (!(k in y)) diffs.push({ path: p, left: x[k], right: undefined, type: 'removed' });
      else compare(x[k], y[k], p);
    }
  }
  compare(a, b, '');
  if (diffs.length === 0) return '✅ 两个 YAML 完全相同';
  return diffs.map(d => {
    if (d.type === 'added') return `+ ${d.path}: ${JSON.stringify(d.right)}`;
    if (d.type === 'removed') return `- ${d.path}: ${JSON.stringify(d.left)}`;
    return `~ ${d.path}: ${JSON.stringify(d.left)} → ${JSON.stringify(d.right)}`;
  }).join('\n');
}

// ─── YAML → C++ ──────────────────────────────────────────────────────────────

export function yamlToCpp(input) {
  const obj = parseYaml(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `    ${getCppType(k, v)} ${toSnakeCase(k)};`);
    structs[name] = `struct ${name} {\n${fields.join('\n')}\n};`;
  }
  function getCppType(key, val) {
    if (val === null) return 'std::nullptr_t';
    if (Array.isArray(val)) return val.length === 0 ? 'std::vector<std::any>' : `std::vector<${getCppType(key, val[0])}>`;
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'double';
    return 'std::string';
  }
  buildStruct('Root', obj);
  return `#include <string>\n#include <vector>\n#include <any>\n\n` + Object.values(structs).reverse().join('\n\n');
}

// ─── TOML → YAML ─────────────────────────────────────────────────────────────

export function tomlToYaml(input) {
  // reuse tomlToJson logic inline (minimal parser)
  const lines = input.split('\n');
  const root = {};
  let current = root;

  for (let line of lines) {
    line = line.trim();
    if (!line || line.startsWith('#')) continue;
    if (line.startsWith('[') && line.endsWith(']') && !line.startsWith('[[')) {
      const path = line.slice(1, -1).trim().split('.');
      current = root;
      for (const p of path) {
        if (!(p in current)) current[p] = {};
        current = current[p];
      }
    } else if (line.includes('=')) {
      const eqIdx = line.indexOf('=');
      const k = line.slice(0, eqIdx).trim();
      let v = line.slice(eqIdx + 1).trim();
      let parsed;
      if (v === 'true') parsed = true;
      else if (v === 'false') parsed = false;
      else if (v.startsWith('"') && v.endsWith('"')) parsed = JSON.parse(v);
      else if (v.startsWith('[') && v.endsWith(']')) { try { parsed = JSON.parse(v.replace(/'/g, '"')); } catch { parsed = v; } }
      else if (!isNaN(Number(v))) parsed = Number(v);
      else parsed = v;
      current[k] = parsed;
    }
  }
  return serializeYaml(root, 0);
}

// ─── YAML → Dart ─────────────────────────────────────────────────────────────

export function yamlToDart(input) {
  const obj = parseYaml(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `  final ${getDartType(k, v)} ${toCamelCase(k)};`);
    const params = Object.keys(o).map(k => `    required this.${toCamelCase(k)},`);
    classes[name] = `class ${name} {\n${fields.join('\n')}\n\n  const ${name}({${params.join('\n')}\n  });\n\n  factory ${name}.fromJson(Map<String, dynamic> json) => ${name}(\n${Object.entries(o).map(([k]) => `    ${toCamelCase(k)}: json['${k}'],`).join('\n')}\n  );\n}`;
  }
  function getDartType(key, val) {
    if (val === null) return 'dynamic';
    if (Array.isArray(val)) return val.length === 0 ? 'List<dynamic>' : `List<${getDartType(key, val[0])}>`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'double';
    return 'String';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── YAML → Go ───────────────────────────────────────────────────────────────

export function yamlToGo(input) {
  const obj = parseYaml(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `\t${toPascalCase(k)} ${getGoType(k, v)} \`yaml:"${k}"\``);
    structs[name] = `type ${name} struct {\n${fields.join('\n')}\n}`;
  }
  function getGoType(key, val) {
    if (val === null) return 'interface{}';
    if (Array.isArray(val)) return val.length === 0 ? '[]interface{}' : `[]${getGoType(key, val[0])}`;
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'int' : 'float64';
    return 'string';
  }
  buildStruct('Root', obj);
  return `package main\n\n` + Object.values(structs).reverse().join('\n\n');
}

// ─── YAML Formatter ───────────────────────────────────────────────────────────

export function yamlFormat(input) {
  const obj = parseYaml(input);
  return serializeYaml(obj, 0);
}

// ─── YAML → SQL Schema ────────────────────────────────────────────────────────

export function yamlToSqlSchema(input) {
  const obj = parseYaml(input);
  const arr = Array.isArray(obj) ? obj : [obj];
  const sample = arr[0] || {};
  const cols = Object.entries(sample).map(([k, v]) => {
    let t = 'TEXT';
    if (typeof v === 'boolean') t = 'BOOLEAN';
    else if (typeof v === 'number') t = Number.isInteger(v) ? 'INTEGER' : 'FLOAT';
    else if (typeof v === 'object' && v !== null) t = 'JSONB';
    return `  ${toSnakeCase(k)} ${t}`;
  });
  return `CREATE TABLE root (\n  id SERIAL PRIMARY KEY,\n${cols.join(',\n')}\n);`;
}

// ─── YAML → Kotlin ───────────────────────────────────────────────────────────

export function yamlToKotlin(input) {
  const obj = parseYaml(input);
  const classes = {};
  function buildClass(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const params = Object.entries(o).map(([k, v]) => `    val ${toCamelCase(k)}: ${getKotlinType(k, v)}`);
    classes[name] = `data class ${name}(\n${params.join(',\n')}\n)`;
  }
  function getKotlinType(key, val) {
    if (val === null) return 'Any?';
    if (Array.isArray(val)) return val.length === 0 ? 'List<Any>' : `List<${getKotlinType(key, val[0])}>`;
    if (typeof val === 'object') { buildClass(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Boolean';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int' : 'Double';
    return 'String';
  }
  buildClass('Root', obj);
  return Object.values(classes).reverse().join('\n\n');
}

// ─── YAML → Objective-C ───────────────────────────────────────────────────────

export function yamlToObjC(input) {
  const obj = parseYaml(input);
  const interfaces = {};
  function buildInterface(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const props = Object.entries(o).map(([k, v]) => `@property (nonatomic, strong) ${getObjCType(k, v)} *${toCamelCase(k)};`);
    interfaces[name] = `@interface ${name} : NSObject\n${props.join('\n')}\n@end`;
  }
  function getObjCType(key, val) {
    if (val === null) return 'id';
    if (Array.isArray(val)) return 'NSArray';
    if (typeof val === 'object') { buildInterface(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'NSNumber';
    if (typeof val === 'number') return 'NSNumber';
    return 'NSString';
  }
  buildInterface('Root', obj);
  return `#import <Foundation/Foundation.h>\n\n` + Object.values(interfaces).reverse().join('\n\n');
}

// ─── YAML → JSON ─────────────────────────────────────────────────────────────

export function yamlToJson(input) {
  const obj = parseYaml(input);
  return JSON.stringify(obj, null, 2);
}

// ─── YAML → BigQuery ─────────────────────────────────────────────────────────

export function yamlToBigQuery(input) {
  const obj = parseYaml(input);
  function toBqFields(o) {
    return Object.entries(o).map(([k, v]) => {
      if (Array.isArray(v)) {
        const inner = v.length > 0 && typeof v[0] === 'object' ? { type: 'RECORD', fields: toBqFields(v[0]) } : { type: getBqType(v[0]) };
        return { name: k, mode: 'REPEATED', ...inner };
      }
      if (typeof v === 'object' && v !== null) return { name: k, type: 'RECORD', mode: 'NULLABLE', fields: toBqFields(v) };
      return { name: k, type: getBqType(v), mode: 'NULLABLE' };
    });
  }
  function getBqType(val) {
    if (val === null) return 'STRING';
    if (typeof val === 'boolean') return 'BOOL';
    if (typeof val === 'number') return Number.isInteger(val) ? 'INT64' : 'FLOAT64';
    return 'STRING';
  }
  return JSON.stringify(toBqFields(obj), null, 2);
}

// ─── YAML → Zod Schema ───────────────────────────────────────────────────────

export function yamlToZod(input) {
  const obj = parseYaml(input);
  const schemas = {};
  function buildSchema(name, val) {
    if (typeof val !== 'object' || val === null || Array.isArray(val)) return zodType(name, val);
    const fields = Object.entries(val).map(([k, v]) => `  ${k}: ${buildSchema(k, v)},`);
    schemas[name] = `const ${toCamelCase(name)}Schema = z.object({\n${fields.join('\n')}\n});`;
    return `${toCamelCase(name)}Schema`;
  }
  function zodType(key, val) {
    if (val === null) return 'z.null()';
    if (Array.isArray(val)) return val.length === 0 ? 'z.array(z.any())' : `z.array(${zodType(key, val[0])})`;
    if (typeof val === 'object') { buildSchema(toPascalCase(key), val); return `${toCamelCase(key)}Schema`; }
    if (typeof val === 'boolean') return 'z.boolean()';
    if (typeof val === 'number') return Number.isInteger(val) ? 'z.number().int()' : 'z.number()';
    return 'z.string()';
  }
  buildSchema('Root', obj);
  return `import { z } from 'zod';\n\n` + Object.values(schemas).reverse().join('\n\n') + `\n\nexport type Root = z.infer<typeof rootSchema>;`;
}

// ─── YAML → Crystal ──────────────────────────────────────────────────────────

export function yamlToCrystal(input) {
  const obj = parseYaml(input);
  const structs = {};
  function buildStruct(name, o) {
    if (typeof o !== 'object' || o === null || Array.isArray(o)) return;
    const fields = Object.entries(o).map(([k, v]) => `  property ${toSnakeCase(k)} : ${getCrystalType(k, v)}`);
    structs[name] = `struct ${name}\n${fields.join('\n')}\nend`;
  }
  function getCrystalType(key, val) {
    if (val === null) return 'Nil';
    if (Array.isArray(val)) return val.length === 0 ? 'Array(YAML::Any)' : `Array(${getCrystalType(key, val[0])})`;
    if (typeof val === 'object') { buildStruct(toPascalCase(key), val); return toPascalCase(key); }
    if (typeof val === 'boolean') return 'Bool';
    if (typeof val === 'number') return Number.isInteger(val) ? 'Int32' : 'Float64';
    return 'String';
  }
  buildStruct('Root', obj);
  return Object.values(structs).reverse().join('\n\n');
}
